'use client'

import { Fragment, ReactNode, useId, useLayoutEffect, useMemo, useState } from 'react'
import { AnimatePresence, motion, useWillChange } from 'framer-motion'
import { CheckCircle2 } from 'lucide-react'
import { run } from 'shared'
import { LoadingSpinner } from '@/components/ui/loading-spinner'
import { useMeasure } from 'react-use'
import { IChildrenProps } from '@/utils/typings/children-props'
import { useDynamicIslandContext } from '@/features/dynamic-island/dynamic-island-context'
import CompletionCircle from '@/features/animations/completion-circle'

type IDynamicIslandItemVisualState = 'idle' | 'start' | 'content' | 'final-remark' | 'closing'
type IDynamicIslandItemMappedState = Extract<IDynamicIslandItemVisualState, 'idle' | 'content' | 'final-remark'>
type IDynamicIslandItemStateMap<T extends string> = Record<T, IDynamicIslandItemMappedState>

export interface IDynamicIslandItemProps<T extends string = string> extends IChildrenProps {
    stateMap: IDynamicIslandItemStateMap<T>
    state: T
    icon?:
        | { node: ReactNode; id: string }
        | 'check'
        | 'loading'
        | { type: 'completion'; total: number; current: number }
    render?: (props: { step: IDynamicIslandItemVisualState; completedWidthAnimation: boolean; state: T }) => ReactNode
    id: string
    openingTimeoutMs?: number
    closingTimeoutMs?: number
}

export class DynamicIslandHelpers {
    static mapStateToStep<T extends string>(
        state: T,
        stateMap: IDynamicIslandItemStateMap<T>
    ): IDynamicIslandItemVisualState {
        return (Object.entries(stateMap).find(([key]) => state === key)?.[1] ??
            ('idle' as const)) as IDynamicIslandItemVisualState
    }
}

export const DynamicIslandItem = <T extends string>({
    stateMap,
    state,
    icon,
    children,
    render,
    id,
    openingTimeoutMs = 600,
    closingTimeoutMs = 600,
    openCount,
    index,
}: IDynamicIslandItemProps<T> & { openCount: number; index: number }) => {
    const componentId = useId()
    const [xRef, { width: xWidth }] = useMeasure<HTMLDivElement>()

    const { clearFromState } = useDynamicIslandContext()

    const [completedWidthAnimation, setCompletedWidthAnimation] = useState(false)

    const willChange = useWillChange()

    const [step, setStep] = useState<IDynamicIslandItemVisualState>('idle')

    const mappedStep = useMemo(() => DynamicIslandHelpers.mapStateToStep(state, stateMap), [state, stateMap])

    useLayoutEffect(() => {
        let closingTimeout: NodeJS.Timeout | null = null
        let closingIdleTimeout: NodeJS.Timeout | null = null
        let startContentTimeout: NodeJS.Timeout | null = null

        // We reverse it in here so that we can define the props more conveniently from the outside
        // convenient in the sense, that the stateMap prop knows which keys it accepts without having to pass a generic first

        if (mappedStep === 'idle') {
            if (step !== 'idle') {
                closingTimeout = setTimeout(() => {
                    if (openCount > 1) {
                        setStep('idle')
                    } else {
                        setStep('closing')

                        closingIdleTimeout = setTimeout(() => {
                            setStep('idle')
                        }, closingTimeoutMs)
                    }
                }, 100)
            }
        } else if (mappedStep === 'content' || mappedStep === 'final-remark') {
            if (step === 'idle' || step === 'start') {
                if (!startContentTimeout) {
                    setStep('start')

                    startContentTimeout = setTimeout(() => {
                        setStep('content')
                    }, openingTimeoutMs)
                }
            }
        }

        return () => {
            if (closingTimeout) {
                clearTimeout(closingTimeout)
                closingTimeout = null
            }

            if (closingIdleTimeout) {
                clearTimeout(closingIdleTimeout)
                closingIdleTimeout = null
            }

            if (startContentTimeout) {
                clearTimeout(startContentTimeout)
                startContentTimeout = null
            }
        }
    }, [mappedStep, step, openingTimeoutMs, closingTimeoutMs, openCount])

    const hasIconNode = Boolean(
        icon && (typeof icon === 'string' || !!('node' in icon && icon?.node) || 'type' in icon)
    )

    const hasChildren = (Boolean(!!children || render) && step === 'content') || step === 'final-remark'

    return (
        <AnimatePresence onExitComplete={() => clearFromState(id)}>
            {step !== 'idle' && (
                <Fragment>
                    <AnimatePresence>
                        {index > 0 && (step === 'start' || step === 'content' || step === 'final-remark') && (
                            <motion.div
                                initial={{ width: 0, opacity: 0 }}
                                animate={{ width: 40, opacity: 1 }}
                                exit={{ width: 0, opacity: 0, transition: { delay: 0 } }}
                                transition={{
                                    width: { type: 'spring', damping: 8, mass: 0.02, delay: 0.1 },
                                    opacity: { duration: 0.1, delay: 0.1 },
                                }}
                                className="bg-transparent overflow-hidden flex items-center justify-center"
                            >
                                <motion.div className="h-4 min-h-4 w-px min-w-px bg-white/60" />
                            </motion.div>
                        )}
                    </AnimatePresence>

                    <motion.div
                        initial={{ width: 'fit-content' }}
                        animate={{ width: hasIconNode || hasChildren ? 'fit-content' : 0 }}
                        transition={{ type: 'spring', damping: 8, mass: 0.02 }}
                        exit={{ width: 0 }}
                        className="overflow-hidden flex items-center"
                    >
                        <AnimatePresence key="directory-file-upload-progress-animate-presence-1" mode="wait">
                            {icon &&
                                (typeof icon === 'string' || !!('node' in icon && icon?.node) || 'type' in icon) && (
                                    <motion.div
                                        key={
                                            'dynamic-island-2' +
                                            componentId +
                                            'icon' +
                                            (typeof icon === 'string' ? icon : 'id' in icon ? icon.id : icon.type)
                                        }
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        exit={{ opacity: 0 }}
                                        className="flex items-center"
                                    >
                                        {run(() => {
                                            if (typeof icon === 'string') {
                                                if (icon === 'loading')
                                                    return <LoadingSpinner size={20} colorClassName="stroke-white" />

                                                if (icon === 'check')
                                                    return <CheckCircle2 className="h-5 w-5 text-[#00ffad]" />

                                                return null
                                            }

                                            if ('type' in icon) {
                                                if (icon.type === 'completion') {
                                                    return (
                                                        <CompletionCircle
                                                            sqSize={16}
                                                            strokeWidth={2}
                                                            percentage={(icon.current / icon.total) * 100}
                                                        />
                                                    )
                                                } else {
                                                    return null
                                                }
                                            }
                                            return icon.node
                                        })}
                                    </motion.div>
                                )}
                        </AnimatePresence>

                        <AnimatePresence key={'dynamic-island-3' + componentId} mode="sync">
                            {(step === 'content' || step === 'final-remark') && (
                                <motion.div
                                    initial={{ width: 0 }}
                                    animate={{ width: xWidth ? xWidth : 'auto' }}
                                    onAnimationComplete={() => setCompletedWidthAnimation(true)}
                                    exit={{
                                        width: 0,
                                        opacity: 0,
                                        transition: {
                                            opacity: { duration: 0.1 },
                                            width: { duration: 0.5, type: 'spring', bounce: 0.05 },
                                        },
                                    }}
                                    style={{ overflow: 'hidden', willChange }}
                                    key={'dynamic-island-4' + componentId}
                                    className="origin-center"
                                    transition={{ type: 'spring', damping: 8, mass: 0.02 }}
                                >
                                    <div ref={xRef} className="flex items-center whitespace-nowrap min-w-fit">
                                        <AnimatePresence mode="wait" initial>
                                            <motion.div
                                                key={'dynamic-island-5' + componentId}
                                                initial={{ opacity: 0 }}
                                                animate={{ opacity: 1, transition: { delay: 0.1, duration: 0.5 } }}
                                                exit={{ opacity: 0 }}
                                                className="flex items-center"
                                            >
                                                <AnimatePresence mode="wait" initial={false}>
                                                    {(!!children || render) && hasIconNode && (
                                                        <motion.div
                                                            initial={{ width: 0 }}
                                                            animate={{ width: 8 }}
                                                            exit={{ width: 0 }}
                                                            key={'dynamic-island-6' + componentId}
                                                            className="bg-transparent overflow-hidden"
                                                        />
                                                    )}
                                                </AnimatePresence>

                                                {render ? render({ completedWidthAnimation, state, step }) : children}
                                            </motion.div>
                                        </AnimatePresence>
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </motion.div>
                </Fragment>
            )}
        </AnimatePresence>
    )
}
